import type { CampaignMetricsSnapshot } from "src/actions/ads";
import { dineroFormatCurrency } from "src/utils";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

/** Campaign lifecycle status → dot color (used only when performance_tier is missing). */
export const STATUS_COLOR_SCHEME: Record<string, string> = {
  ACTIVE: "green",
  SCHEDULED: "blue",
  PAUSED: "orange",
  COMPLETED: "gray",
  ARCHIVED: "gray",
  DRAFT: "purple",
  UNKNOWN: "gray",
};

/** Performance tier → dot color for Active Campaigns list and legend. Single source of truth for all performance dots. */
export const PERFORMANCE_TIER_COLOR_SCHEME: Record<string, string> = {
  HIGH_PERFORMANCE: "green.500",
  UNDERPERFORMING: "red.500",
  ON_TRACK: "blue.500",
  TOO_EARLY_TO_TELL: "gray.400",
};

/** Legend items derived from PERFORMANCE_TIER_COLOR_SCHEME so legend and row dots always use the same colors. */
export const PERFORMANCE_TIER_LEGEND: {
  tier: keyof typeof PERFORMANCE_TIER_COLOR_SCHEME;
  label: string;
}[] = [
  { tier: "HIGH_PERFORMANCE", label: "High Performing" },
  { tier: "UNDERPERFORMING", label: "Underperforming" },
  { tier: "ON_TRACK", label: "On Track" },
  { tier: "TOO_EARLY_TO_TELL", label: "Too Early To Tell" },
];

export type AdCampaignSortOption =
  | "updated_at_descending"
  | "created_at_descending"
  | "event_start_at_ascending"
  | "event_start_at_descending"
  | "name_ascending";

export function sortToIndexSuffix(sort: AdCampaignSortOption): string | null {
  switch (sort) {
    case "updated_at_descending":
      return null;
    case "created_at_descending":
      return "created_at_descending";
    case "event_start_at_ascending":
      return "event_start_at_ascending";
    case "event_start_at_descending":
      return "event_start_at_descending";
    case "name_ascending":
      return "name_ascending";
    default:
      return null;
  }
}

export function formatDraftLastEdited(
  updatedAt: string | undefined
): string {
  if (!updatedAt?.trim()) return "—";
  const parsed = dayjs(updatedAt);
  if (!parsed.isValid()) return "—";
  const diffSeconds = dayjs().diff(parsed, "second");
  if (diffSeconds < 60) return "Just now";
  const relative = parsed.fromNow(true);
  return relative.charAt(0).toUpperCase() + relative.slice(1) + " ago";
}

export function formatDraftBudget(displayBudget: string | undefined): string {
  const trimmed = displayBudget?.trim();
  return trimmed ? trimmed : "Not set";
}

export function orgMetricsToDisplay(snapshot: CampaignMetricsSnapshot | null): {
  updated_at: string;
  displaySpend: string;
  displayRoas: string;
  displayTickets: string;
  displayCtr: string;
} | null {
  if (!snapshot) return null;
  const ctr = snapshot.ctr;
  const displayCtr =
    ctr != null && Number.isFinite(ctr) && ctr > 0
      ? `${Number(ctr).toFixed(2)}%`
      : "-";

  return {
    updated_at: snapshot.updated_at,
    displaySpend:
      snapshot.spend > 0
        ? dineroFormatCurrency(Math.round(snapshot.spend * 100))
        : "$0",
    displayRoas: Number.isFinite(snapshot.roas) ? `${snapshot.roas.toFixed(2)}x` : "-",
    displayTickets: snapshot.conversions > 0 ? snapshot.conversions.toLocaleString() : "0",
    displayCtr,
  };
}
